import React, { useState, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { 
  Card, Table, Tag, Button, Space, Typography, DatePicker, 
  Select, Input, Tooltip, message, Progress, Empty, Alert,
  Tabs, Radio, Switch, Divider, Row, Col, Spin, Statistic,
  Breadcrumb, List, Popover, Layout, Menu, Modal, Checkbox
} from 'antd';
import { 
  ArrowLeftOutlined, 
  ImportOutlined, 
  FileExcelOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  AppstoreOutlined,
  CarOutlined,
  BarChartOutlined,
  FieldTimeOutlined,
  UserOutlined,
  ScheduleOutlined,
  PieChartOutlined,
  LineChartOutlined,
  HeatMapOutlined,
  HomeOutlined,
  LeftOutlined,
  RightOutlined,
  BulbOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import OTRDataService from './utils/OTRDataService';
import { OTR_THRESHOLDS } from './utils/OTRDataService';
import { toHrsMinsSecs, toTime } from '../../libs/formatLib';
import { ProposedOTR } from './components/ProposedOTR';
import OTRStatisticalVarianceChart from './components/OTRStatisticalVarianceChart';
import OTRCharts from './components/OTRCharts';
import OTRImporter from './components/OTRImporter';
import OTRPerformanceRadialChart from './components/OTRPerformanceRadialChart';
import OTRMultiTripChart from './components/OTRMultiTripChart';
import OTRTripDetailView from './components/OTRTripDetailView';
import OTRDateRangePicker from './components/OTRDateRangePicker';
import OTRDailyOverviewTab from './components/OTRDailyOverviewTab';
import OTRInsightCard from './components/OTRInsightCard';
import OTRPerformanceOverviewTab from './components/OTRPerformanceOverviewTab';
import OTRStopPerformanceTab from './components/OTRStopPerformanceTab';
import OTRDriverPerformanceTab from './components/OTRDriverPerformanceTab';
import OTRTripPerformanceTab from './components/OTRTripPerformanceTab';
import OTRTabs from './components/OTRTab';
import { 
  analyzeTripPerformance, 
  analyzeStopPerformance, 
  analyzeDriverPerformance,
  generateSummaryInsights,
  TIME_PERIODS
} from './utils/OTRDashboardAnalytics';
import { 
  analyzeTripWithRuleset, 
  analyzeStopWithRuleset, 
  analyzeDriverWithRuleset,
  analyzeDailyPerformanceWithRuleset
} from './utils/OTRKPIAIRuleset';
import otrDataProcessor from './utils/OTRDataProcessor';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ReferenceLine,
  ResponsiveContainer 
} from 'recharts';
import TrackedTab from './components/TrackedTab';
import { Close } from '../../components/Icons';
import useAllEmployees from '../../hooks/useAllEmployees';
import { values } from 'lodash';
import { employeeModelData } from '../../services/ModelService';

const { TabPane } = Tabs;
const { Text, Title, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// View level constants
const VIEW_LEVELS = {
  TRIP_AGGREGATE: 1,   // Level 1: Aggregate trip performance
  TRIP_BY_DATE: 2,     // Level 2: Trip performance by date
  TRIP_DETAIL: 3,      // Level 3: Trip detail (stop-by-stop)
  STOP_DETAIL: 4       // Level 4: Stop detail (showing trips for a specific stop)
};

// Global debug function for the browser console
window.checkBusableStopTSN = async (tsn) => {
  try {
    return await OTRDataService.checkTSNExists(tsn);
  } catch (error) {
    console.error('Error checking TSN:', error);
    return false;
  }
};

const OTRDashboard = () => {
  /**
   * OTR Dashboard provides a three-level hierarchical view of OTR performance:
   * 
   * Level 1: Trip Aggregate - Shows aggregate trip performance across the selected time period
   *   - Each row represents a unique trip with aggregate performance metrics
   *   - No date column (time period is in the header)
   * 
   * Level 2: Trip by Date - Shows performance for a specific trip broken down by date
   *   - Each row represents the same trip on a different date
   *   - Date column is displayed
   *   - Shows first/mid/last stop OTR metrics for each date
   * 
   * Level 3: Trip Detail - Shows all stops for a specific trip on a specific date
   *   - Each row represents a stop in the trip's stopping pattern
   *   - Displays stops in sequence order
   *   - Shows OTR performance for each stop
   * 
   * Data Loading Strategy:
   * - Date-filtered data is loaded once when dateRange changes via OTRDataService
   * - Other filters (route, driver, etc.) are applied locally to the date-filtered dataset
   * - This improves performance by avoiding repeated API calls for filter changes
   */
  const [loading, setLoading] = useState(true);
  const [dateRange, _setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs().endOf('day')]);
  const setDateRange = (range) => {
    if (Array.isArray(range) && range.length === 2 && range[1]) {
      _setDateRange([range[0], range[1].endOf('day')]);
    } else {
      _setDateRange(range);
    }
  };
  const [selectedDatePreset, setSelectedDatePreset] = useState('last30');
  const [tripData, setTripData] = useState([]);
  const [viewLevel, setViewLevel] = useState(VIEW_LEVELS.TRIP_AGGREGATE);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [showImporter, setShowImporter] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [reportData, setReportData] = useState(null);

  // Filter states
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectedRouteTypes, setSelectedRouteTypes] = useState([]);
  const [selectedDirections, setSelectedDirections] = useState([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('all');
  const [selectedTripFilters, setSelectedTripFilters] = useState([]);
  const [stopSearchText, setStopSearchText] = useState('');
  const [treatSchoolOutEarlyAsOnTime, setTreatSchoolOutEarlyAsOnTime] = useState(true);
  
  // Active filter states (used for data loading)
  const [activeRoutes, setActiveRoutes] = useState([]);
  const [activeDrivers, setActiveDrivers] = useState([]);
  const [activeRouteTypes, setActiveRouteTypes] = useState([]);
  const [activeDirections, setActiveDirections] = useState([]);
  const [activeTripFilters, setActiveTripFilters] = useState([]);
  
  // Available filter options (populated after data load)
  const [filterOptions, setFilterOptions] = useState({
    routes: [],
    drivers: [],
    trips: [],
    routeTypes: [],
    directions: []
  });

  // Additional data states
  const [stopData, setStopData] = useState([]);
  const [driverData, setDriverData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    insights: [],
    recommendations: []
  });

  // Date-filtered dataset for local filtering
  const [dateFilteredDataset, setDateFilteredDataset] = useState([]);
  const [isDateDatasetLoaded, setIsDateDatasetLoaded] = useState(false);

  // In the state declarations section, after other state variables
  const [selectedStopTypes, setSelectedStopTypes] = useState(['ALL']); // Default to "All" stops
  const [driverTripData, setDriverTripData] = useState([]); 
  const [selectedDriverDetail, setSelectedDriverDetail] = useState(null); // For driver detail view

  // State to store trip start times
  const [tripStartTimes, setTripStartTimes] = useState({});

  // Top-level tab state
  const [mainTab, setMainTab] = useState('otr');

  // Employee data for driver names
  const { allEmployees } = useAllEmployees();
  const [allEmployeeRecords, setAllEmployeeRecords] = useState(null);

  // Load all employee records including details
  useEffect(() => {
    const loadAllEmployeeRecords = async () => {
      try {
        const allRecords = await employeeModelData.getAll();
        setAllEmployeeRecords(allRecords);
      } catch (error) {
        console.error('Error loading employee records:', error);
      }
    };

    loadAllEmployeeRecords();
  }, []);

  // Function to get driver name by driver ID (which maps to Driver Authority Number)
  const getDriverName = (driverId) => {
    if (!allEmployeeRecords || !driverId) return 'Unknown Driver';
    
    // Get all employee records and separate them by type
    const xeroEmployeeArray = values(allEmployeeRecords).filter(e => e.sk && e.sk.endsWith('#EXTERNAL') && !e.isDeleted);
    const detailsEmployeeArray = values(allEmployeeRecords).filter(e => e.sk && e.sk.endsWith('#DETAILS') && !e.isDeleted);
    
    // Find the employee details record that has matching driver authority number
    const employeeDetailsRecord = detailsEmployeeArray.find(record => 
      String(record.daNumber) === String(driverId)
    );
    
    if (employeeDetailsRecord) {
      // Extract employeeID from the details record key
      const employeeID = employeeDetailsRecord.sk.match(/#EMPLOYEE#(.+)#DETAILS/)?.[1];
      
      if (employeeID) {
        // Find the corresponding external record
        const employeeExternal = xeroEmployeeArray.find(emp => 
          emp.employeeID === employeeID
        );
        
        if (employeeExternal && employeeExternal.firstName && employeeExternal.lastName) {
          return `${employeeExternal.firstName} ${employeeExternal.lastName}`;
        }
      }
    }
    
    return `Driver ${driverId}`;
  };

  // Load trip start times for all trips in the date-filtered dataset
  useEffect(() => {
    const loadAllTripStartTimes = async () => {
      // Use date-filtered dataset if available, otherwise get all data
      let allData = dateFilteredDataset;
      if (!allData || allData.length === 0) {
        allData = await OTRDataService.retrieveAllData();
      }
      if (!Array.isArray(allData)) return;
      
      const times = {};
      
      // Get all unique trip keys from the data
      let uniqueTrips = [...new Set(allData.map(item => otrDataProcessor.getTripKey(item)))].filter(Boolean);
      
      // Filter trips by selected route if a route is selected
      if (activeRoutes.length > 0) {
        const routeTrips = new Set();
        allData.forEach(item => {
          const tripKey = otrDataProcessor.getTripKey(item);
          if (activeRoutes.some(route => String(item.ROUTE) === String(route)) && tripKey) {
            routeTrips.add(tripKey);
          }
        });
        uniqueTrips = Array.from(routeTrips);
      }
      
      console.log(`Loading trip start times for ${uniqueTrips.length} unique trips`);
      
      // Sort trips by start time (now embedded in KEY)
      uniqueTrips.sort((a, b) => {
        // Extract start time from new KEY format: ROUTE_VARIANT_STARTTIME_CONTRACT_CODE
        const extractStartTime = (key) => {
          if (!key || typeof key !== 'string') return '00:00';
          const parts = key.split('_');
          if (parts.length >= 3) {
            // Second-to-last part should be the start time (HH:mm)
            const timeCandidate = parts[parts.length - 2];
            // Check if it looks like a time (HH:mm format)
            if (timeCandidate && timeCandidate.match(/^\d{2}:\d{2}$/)) {
              return timeCandidate;
            }
          }
          return '00:00';
        };
        
        const timeA = extractStartTime(a);
        const timeB = extractStartTime(b);
        
        return timeA.localeCompare(timeB);
      });
      
      for (const tripKey of uniqueTrips) {
        const tripRecords = allData.filter(r => otrDataProcessor.getTripKey(r) === tripKey);
        if (tripRecords.length === 0) continue;
        
        // Extract start time from new KEY format: ROUTE_VARIANT_STARTTIME_CONTRACT_CODE
        const extractStartTimeFromKey = (key) => {
          if (!key || typeof key !== 'string') return null;
          const parts = key.split('_');
          if (parts.length >= 3) {
            // Second-to-last part should be the start time (HH:mm)
            const timeCandidate = parts[parts.length - 2];
            // Check if it looks like a time (HH:mm format)
            if (timeCandidate && timeCandidate.match(/^\d{2}:\d{2}$/)) {
              return timeCandidate;
            }
          }
          return null;
        };
        
        let timeValue = extractStartTimeFromKey(tripKey);
        
        // If we can't extract from KEY (legacy format), fall back to first stop method
        if (!timeValue) {
          const firstStop = tripRecords.find(r => r.TSN_FIRST_LAST_IND === 'F');
          if (firstStop) {
            let timeSeconds = firstStop.SCHEDULED_DEPARTURE_TIME || firstStop.S_DEP;
            if (!timeSeconds) {
              timeSeconds = firstStop.SCHEDULED_ARRIVAL_TIME || firstStop.S_ARR;
            }
            if (timeSeconds) {
              timeValue = toTime(timeSeconds);
            }
          }
        }
        
        if (timeValue) {
          const firstRecord = tripRecords[0]; // Get any record for route and tripId info
          times[tripKey] = {
            time: timeValue,
            route: firstRecord.ROUTE,
            tripId: firstRecord.TRIP_ID // Keep original TRIP_ID for display
          };
        }
      }
      
      console.log(`Stored ${Object.keys(times).length} trip start times`);
      setTripStartTimes(times);
    };
    
    loadAllTripStartTimes();
  }, [dateFilteredDataset, activeRoutes, isDateDatasetLoaded]); // Run when date-filtered dataset changes

  // Clear selected trip filter when route changes
  useEffect(() => {
    const checkAndClearTripFilter = async () => {
      if (activeRoutes.length > 0 && activeTripFilters.length > 0) {
        // Check if the selected trip is still valid for the new route
        const allData = await OTRDataService.retrieveAllData();
        if (allData && Array.isArray(allData)) {
          const tripExistsForRoute = allData.some(item => 
            activeTripFilters.some(tripKey => otrDataProcessor.getTripKey(item) === tripKey && activeRoutes.some(route => String(item.ROUTE) === String(route)))
          );
          if (!tripExistsForRoute) {
            setActiveTripFilters([]);
            setSelectedTripFilters([]);
          }
        }
      }
    };
    
    checkAndClearTripFilter();
  }, [selectedRoutes, selectedTripFilters]);

  // Use centralized data processor for filtering
  const filterDataByStopType = (data) => {
    return otrDataProcessor.filterDataByStopType(data, selectedStopTypes);
  };

  // Simplify date range handling for the new component
  const handleDateRangeChange = (dates) => {
    if (dates && Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
      console.log("Setting date range:", dates[0].format('YYYY-MM-DD'), 'to', dates[1].format('YYYY-MM-DD'));
      
      // If the dates match any of our presets, set the selectedDatePreset value
      const today = dayjs();
      const dateStart = dates[0];
      const dateEnd = dates[1];
      
      // Check for "Last 30 days"
      const thirtyDaysAgo = today.clone().subtract(30, 'day').startOf('day');
      if (dateStart.isSame(thirtyDaysAgo, 'day') && dateEnd.isSame(today, 'day')) {
        setSelectedDatePreset('last30');
        return;
      }
      
      // Check for "Last 60 days"
      const sixtyDaysAgo = today.clone().subtract(60, 'day').startOf('day');
      if (dateStart.isSame(sixtyDaysAgo, 'day') && dateEnd.isSame(today, 'day')) {
        setSelectedDatePreset('last60');
        return;
      }
      
      // Check for "Last 90 days"
      const ninetyDaysAgo = today.clone().subtract(90, 'day').startOf('day');
      if (dateStart.isSame(ninetyDaysAgo, 'day') && dateEnd.isSame(today, 'day')) {
        setSelectedDatePreset('last90');
        return;
      }
      
      // This is a custom range
      setSelectedDatePreset('custom');
    }
  };

  // Add useEffect to set the default date range on component mount
  useEffect(() => {
    // Set default to "Last 30 days"
    const today = dayjs().endOf('day');
    const thirtyDaysAgo = today.subtract(30, 'day');
    setDateRange([thirtyDaysAgo, today]);
    
    // Log for debugging
    console.log('Setting initial date range:', thirtyDaysAgo.format('YYYY-MM-DD'), 'to', today.format('YYYY-MM-DD'));
  }, []);

  // Load data when component mounts or when filters change
  // REMOVED: This useEffect has been replaced with separate date loading and local filtering
  // useEffect(() => {
  //   if (dateRange && dateRange[0] && dateRange[1]) {
  //     loadData();
  //   }
  // }, [dateRange, activeRoutes, activeDrivers, activeDepots, activeOperatorIds, activeContracts, selectedTimePeriod, activeTripFilters, showTrackedOnly]);

  // Load date-filtered data when dateRange changes
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      loadDateFilteredData();
    }
  }, [dateRange]);

  // Apply local filters when active filters change (but not dateRange)
  // Note: showTrackedOnly and treatSchoolOutEarlyAsOnTime are now handled within OTRTab.js
  useEffect(() => {
    if (isDateDatasetLoaded && dateFilteredDataset.length > 0) {
      applyLocalFilters();
    }
  }, [activeRoutes, activeDrivers, activeRouteTypes, activeDirections, selectedTimePeriod, activeTripFilters, selectedStopTypes, isDateDatasetLoaded]);

  // const loadData = async () => {
  //   setLoading(true);
  //   try {
  //     // Log date range for debugging
  //     if (dateRange && dateRange[0] && dateRange[1]) {
  //       console.log('Loading data with date range:', 
  //         dateRange[0].format('YYYY-MM-DD'), 'to', 
  //         dateRange[1].format('YYYY-MM-DD'));
  //     }
      
  //     // Prepare filters
  //     const filters = {
  //       dateRange,
  //       routes: activeRoutes,
  //       drivers: activeDrivers,
  //       timePeriod: selectedTimePeriod,
  //       tripIds: activeTripFilters,
  //       showTrackedOnly
  //     };
      
  //     // Get data from OTRDataService
  //     const data = await OTRDataService.getFilteredDataWithCoordinates(filters);
      
  //     // Debug - check available routes in the data
  //     const availableRoutes = [...new Set(data.map(item => item.ROUTE))].sort((a, b) => a - b);
  //     console.log('Available routes in data:', availableRoutes);
      
  //     // Extract unique values for filter options
  //     const uniqueDrivers = [...new Set(data.map(item => item.DRIVER_ID))].filter(Boolean).sort();
  //     const uniqueDepots = [...new Set(data.map(item => item.DEPOT))].filter(Boolean).sort();
  //     const uniqueOperatorIds = [...new Set(data.map(item => item.OPERATOR_ID))].filter(Boolean).sort();
  //     const uniqueContracts = [...new Set(data.map(item => item.CONTRACT_ID))].filter(Boolean).sort();
  //     const uniqueRouteTypes = [...new Set(data.map(item => item.ROUTE_TYPE_NAME))].filter(Boolean).sort();
  //     const uniqueDirections = [...new Set(data.map(item => item.DIRECTION))].filter(Boolean).sort();

  //     // Extract unique trips with their start times
  //     let uniqueTrips = [...new Set(data.map(item => item.TRIP_ID))].filter(Boolean);
      
  //     // Filter trips by selected route if a route is selected
  //     if (activeRoutes.length > 0) {
  //       const routeTrips = new Set();
  //       data.forEach(item => {
  //         if (activeRoutes.some(route => String(item.ROUTE) === String(route)) && item.TRIP_ID) {
  //           routeTrips.add(item.TRIP_ID);
  //         }
  //       });
  //       uniqueTrips = Array.from(routeTrips);
  //     }
      
  //     // Sort trips by route first, then by time
  //     uniqueTrips.sort((a, b) => {
  //       // Get trip info for sorting
  //       const tripA = tripStartTimes[a];
  //       const tripB = tripStartTimes[b];
        
  //       // If we have route information, sort by route first
  //       if (tripA && tripB && tripA.route && tripB.route) {
  //         const routeComparison = String(tripA.route).localeCompare(String(tripB.route));
  //         if (routeComparison !== 0) {
  //           return routeComparison;
  //         }
  //       }
        
  //       // Then sort by time if available
  //       if (tripA && tripB && tripA.time && tripB.time) {
  //         return tripA.time.localeCompare(tripB.time);
  //       }
        
  //       // Fallback to trip ID sorting
  //       const numA = Number(a);
  //       const numB = Number(b);
  //       if (!isNaN(numA) && !isNaN(numB)) {
  //         return numA - numB;
  //       }
  //       return String(a).localeCompare(String(b));
  //     });

  //     // Calculate dynamic filter options based on current selections
  //     const calculateDynamicFilterOptions = () => {
  //       // Start with all data and apply filters progressively
  //       let filteredData = [...data];
        
  //       // Apply route filter
  //       if (activeRoutes.length > 0) {
  //         filteredData = filteredData.filter(item => activeRoutes.some(route => String(item.ROUTE) === String(route)));
  //       }
        
  //       // Apply trip filter
  //       if (activeTripFilters.length > 0) {
  //         filteredData = filteredData.filter(item => activeTripFilters.some(tripId => String(item.TRIP_ID) === String(tripId)));
  //       }
        
  //       // Apply driver filter
  //       if (activeDrivers.length > 0) {
  //         filteredData = filteredData.filter(item => activeDrivers.some(driver => String(item.DRIVER_ID) === String(driver)));
  //       }
        
  //       // Apply route type filter
  //       if (activeRouteTypes.length > 0) {
  //         filteredData = filteredData.filter(item => 
  //           activeRouteTypes.some(routeType => String(item.ROUTE_TYPE_NAME) === String(routeType))
  //         );
  //       }
        
  //       // Apply direction filter
  //       if (activeDirections.length > 0) {
  //         filteredData = filteredData.filter(item => 
  //           activeDirections.some(direction => String(item.DIRECTION) === String(direction))
  //         );
  //       }
        
  //       // Apply time period filter
  //       if (selectedTimePeriod && selectedTimePeriod !== 'all') {
  //         filteredData = filteredData.filter(item => {
  //           const timePeriod = getTimePeriod(item.SCHEDULED_ARRIVAL_TIME);
  //           return timePeriod === selectedTimePeriod;
  //         });
  //       }
        
  //       // Extract available options from filtered data
  //       const availableRoutes = [...new Set(filteredData.map(item => item.ROUTE))].filter(Boolean).sort((a, b) => a - b);
  //       const availableDrivers = [...new Set(filteredData.map(item => item.DRIVER_ID))].filter(Boolean).sort();
  //       const availableRouteTypes = [...new Set(filteredData.map(item => item.ROUTE_TYPE_NAME))].filter(Boolean).sort();
  //       const availableDirections = [...new Set(filteredData.map(item => item.DIRECTION))].filter(Boolean).sort();
        
  //       // Calculate available trips based on current filters
  //       let availableTrips = [...new Set(filteredData.map(item => item.TRIP_ID))].filter(Boolean);
  //       availableTrips.sort((a, b) => {
  //         // Get trip info for sorting
  //         const tripA = tripStartTimes[a];
  //         const tripB = tripStartTimes[b];
          
  //         // If we have route information, sort by route first
  //         if (tripA && tripB && tripA.route && tripB.route) {
  //           const routeComparison = String(tripA.route).localeCompare(String(tripB.route));
  //           if (routeComparison !== 0) {
  //             return routeComparison;
  //           }
  //         }
          
  //         // Then sort by time if available
  //         if (tripA && tripB && tripA.time && tripB.time) {
  //           return tripA.time.localeCompare(tripB.time);
  //         }
          
  //         // Fallback to trip ID sorting
  //         const numA = Number(a);
  //         const numB = Number(b);
  //         if (!isNaN(numA) && !isNaN(numB)) {
  //           return numA - numB;
  //         }
  //         return String(a).localeCompare(String(b));
  //       });
        
  //       return {
  //         routes: availableRoutes,
  //         drivers: availableDrivers,
  //         routeTypes: availableRouteTypes,
  //         directions: availableDirections,
  //         trips: availableTrips
  //       };
  //     };

  //     const dynamicFilterOptions = calculateDynamicFilterOptions();

  //     // Update filter options
  //     setFilterOptions(prevOptions => ({
  //       ...prevOptions,
  //       routes: availableRoutes,
  //       drivers: dynamicFilterOptions.drivers,
  //       depots: dynamicFilterOptions.depots,
  //       operatorIds: dynamicFilterOptions.operatorIds,
  //       contracts: dynamicFilterOptions.contracts,
  //       routeTypes: dynamicFilterOptions.routeTypes,
  //       directions: dynamicFilterOptions.directions,
  //       trips: dynamicFilterOptions.trips
  //     }));
      
  //     // Clear selected trip filter if it's no longer valid for the selected route
  //     if (selectedTripFilters.length > 0 && !uniqueTrips.some(tripId => selectedTripFilters.includes(tripId))) {
  //       setSelectedTripFilters([]);
  //     }
      
  //     // Validate and clear invalid filter selections
  //     const validateAndClearFilters = () => {
  //       let hasChanges = false;
        
  //       // Check if selected drivers are still valid
  //       if (activeDrivers.length > 0) {
  //         const invalidDrivers = activeDrivers.filter(driver => !dynamicFilterOptions.drivers.includes(driver));
  //         if (invalidDrivers.length > 0) {
  //           setActiveDrivers(activeDrivers.filter(driver => !invalidDrivers.includes(driver)));
  //           hasChanges = true;
  //         }
  //       }
        
  //       // Check if selected trips are still valid
  //       if (activeTripFilters.length > 0) {
  //         const invalidTrips = activeTripFilters.filter(tripId => !dynamicFilterOptions.trips.includes(tripId));
  //         if (invalidTrips.length > 0) {
  //           setActiveTripFilters(activeTripFilters.filter(tripId => !invalidTrips.includes(tripId)));
  //           hasChanges = true;
  //         }
  //       }
        
  //       return hasChanges;
  //     };
      
  //     // Run validation and log if any filters were cleared
  //     const filtersCleared = validateAndClearFilters();
  //     if (filtersCleared) {
  //       console.log('Some filter selections were cleared due to invalid combinations');
  //     }
      
  //     // Log current filter state for debugging
  //     console.log('Current filter state:', {
  //       selectedRoutes,
  //       selectedTripFilters,
  //       selectedDrivers,
  //       activeRoutes,
  //       activeTripFilters,
  //       activeDrivers,
  //       availableOptions: {
  //         routes: availableRoutes.length,
  //         trips: dynamicFilterOptions.trips.length,
  //         drivers: dynamicFilterOptions.drivers.length,
  //         routeTypes: dynamicFilterOptions.routeTypes.length,
  //         directions: dynamicFilterOptions.directions.length
  //       }
  //     });
      
  //     if (data.length === 0) {
  //       // Check if we have suggested dates
  //       const hasSuggestedDates = await OTRDataService.getDataDateRange();
  //       if (hasSuggestedDates) {
  //         const { startDate, endDate } = hasSuggestedDates;
  //         message.warning(
  //           <span>
  //             No data found for the selected filters. 
  //             <Button 
  //               type="link" 
  //               size="small" 
  //               onClick={() => {
  //                 setDateRange([dayjs(startDate), dayjs(endDate)]);
  //                 setSelectedDatePreset('custom');
  //               }}
  //             >
  //               Try {dayjs(startDate).format('MMM D')} to {dayjs(endDate).format('MMM D, YYYY')}
  //             </Button>
  //           </span>
  //         );
  //       } else {
  //       message.warning('No data found for the selected filters');
  //       }
        
  //       setTripData([]);
  //       setStopData([]);
  //       setDriverData([]);
  //       setFilteredData([]);
  //       setSummaryData({
  //         insights: [],
  //         recommendations: []
  //       });
  //       setLoading(false);
  //       return;
  //     }
      
  //     // Process data for trip-level view
  //     const aggregatedTrips = processTripsData(data);
  //     setTripData(aggregatedTrips);
  //     setFilteredData(aggregatedTrips);
      
  //     // Set stop data for map view
  //     setStopData(data);
      
  //     // Process driver data
  //     const driverAnalysis = await OTRDataService.analyzeDriverPerformance(data);
  //     setDriverData(driverAnalysis);
      
  //     // Generate summary insights
  //     const { insights, recommendations } = generateSummaryInsights(
  //       aggregatedTrips, 
  //       data, 
  //       driverAnalysis
  //     );
      
  //     setSummaryData({
  //       insights,
  //       recommendations
  //     });
      
  //     // Reset view to level 1 when data is reloaded
  //     if (viewLevel !== VIEW_LEVELS.TRIP_AGGREGATE) {
  //       setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
  //       setSelectedTrip(null);
  //       setSelectedDate(null);
  //     }
      
  //     message.success(`Loaded ${data.length} records for analysis`);
  //   } catch (error) {
  //     console.error("Error loading data:", error);
  //     message.error("Failed to load OTR data");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Load date-filtered data from OTRDataService
  const loadDateFilteredData = async () => {
    setLoading(true);
    try {
      console.log('Loading date-filtered data:', 
        dateRange[0].format('YYYY-MM-DD'), 'to', 
        dateRange[1].format('YYYY-MM-DD'));
      
      // Only filter by date range
      const dateFilters = {
        dateRange,
      };
      
      // Get date-filtered data from OTRDataService
      const data = await OTRDataService.getFilteredDataWithCoordinates(dateFilters);
      
      console.log(`Loaded ${data.length} records for date range`);
      
      // Debug: Analyze tracking data in the full dataset
      let trackedCount = 0;
      let untrackedCount = 0;
      let partiallyTrackedCount = 0;
      
      data.forEach(item => {
        const hasActualDeparture = Number.isFinite(item.ACTUAL_DEPARTURE_TIME) || Number.isFinite(item.A_DEP);
        const hasActualArrival = Number.isFinite(item.ACTUAL_ARRIVAL_TIME) || Number.isFinite(item.A_ARR);
        
        if (hasActualDeparture && hasActualArrival) {
          trackedCount++;
        } else if (hasActualDeparture || hasActualArrival) {
          partiallyTrackedCount++;
        } else {
          untrackedCount++;
        }
      });
      
      console.log(`Full dataset tracking analysis: Fully tracked: ${trackedCount}, Partially tracked: ${partiallyTrackedCount}, Untracked: ${untrackedCount}`);
      
      // Store the date-filtered dataset
      setDateFilteredDataset(data);
      setIsDateDatasetLoaded(true);
      
      // Apply initial local filters
      applyLocalFilters(data);
      
    } catch (error) {
      console.error("Error loading date-filtered data:", error);
      message.error("Failed to load date-filtered data");
      setDateFilteredDataset([]);
      setIsDateDatasetLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  // Apply local filters to the date-filtered dataset
  const applyLocalFilters = (data = null) => {
    if (!isDateDatasetLoaded && !data) return;
    
    const sourceData = data || dateFilteredDataset;
    if (!sourceData || sourceData.length === 0) {
      setTripData([]);
      setStopData([]);
      setDriverData([]);
      setFilteredData([]);
      return;
    }
    
    console.log('Applying local filters to', sourceData.length, 'records');
    
    // Apply filters locally
    let filteredData = [...sourceData];
    
    // Filter by route
    if (activeRoutes.length > 0) {
      filteredData = filteredData.filter(item => 
        activeRoutes.some(route => String(item.ROUTE) === String(route))
      );
    }
    
    // Filter by trip
    if (activeTripFilters.length > 0) {
      filteredData = filteredData.filter(item => 
        activeTripFilters.some(tripKey => otrDataProcessor.getTripKey(item) === tripKey)
      );
    }
    
    // Filter by driver
    if (activeDrivers.length > 0) {
      filteredData = filteredData.filter(item => 
        activeDrivers.some(driver => String(item.DRIVER_ID) === String(driver))
      );
    }
    
    // Filter by route type
    if (activeRouteTypes.length > 0) {
      filteredData = filteredData.filter(item => 
        activeRouteTypes.some(routeType => String(item.ROUTE_TYPE_NAME) === String(routeType))
      );
    }
    
    // Filter by direction
    if (activeDirections.length > 0) {
      filteredData = filteredData.filter(item => 
        activeDirections.some(direction => String(item.DIRECTION) === String(direction))
      );
    }
    
    // Filter by time period
    if (selectedTimePeriod && selectedTimePeriod !== 'all') {
      filteredData = filteredData.filter(item => {
        const timePeriod = getTimePeriod(item.SCHEDULED_ARRIVAL_TIME);
        return timePeriod === selectedTimePeriod;
      });
    }
    
    // Filter by stop type
    filteredData = filterDataByStopType(filteredData);
    
    // Note: Tracked filtering is now handled within OTRTab.js, not here
    // This ensures TrackedTab gets unfiltered data for tracking analysis
    
    console.log(`Local filtering: ${sourceData.length} -> ${filteredData.length} records`);
    
    // Update filter options based on the full dateFilteredDataset
    updateFilterOptions(dateFilteredDataset);
    
    // Process data for trip-level view
    const aggregatedTrips = processTripsData(filteredData);
    setTripData(aggregatedTrips);
    setFilteredData(aggregatedTrips);
    
    // Set stop data for map view
    setStopData(filteredData);
    console.log('OTRDashboard - Setting stopData with', filteredData.length, 'records');
    
    // Process driver data
    OTRDataService.analyzeDriverPerformance(filteredData).then(driverAnalysis => {
      setDriverData(driverAnalysis);
      
      // Generate summary insights
      const { insights, recommendations } = generateSummaryInsights(
        aggregatedTrips, 
        filteredData, 
        driverAnalysis
      );
      
      setSummaryData({
        insights,
        recommendations
      });
    });
    
    // Reset view to level 1 when filters change
    if (viewLevel !== VIEW_LEVELS.TRIP_AGGREGATE) {
      setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
      setSelectedTrip(null);
      setSelectedDate(null);
    }
  };

  // Update filter options based on available data
  const updateFilterOptions = (allData) => {
    if (!allData || allData.length === 0) return;
    // Use the full dateFilteredDataset for all filter options except trips
    const availableRoutes = [...new Set(allData.map(item => item.ROUTE))].filter(Boolean).sort((a, b) => String(a || '').localeCompare(String(b || '')));
    
    // Get unique driver IDs and create driver options with names
    const uniqueDriverIds = [...new Set(allData.map(item => item.DRIVER_ID))].filter(Boolean);
    const driverOptions = uniqueDriverIds.map(driverId => {
      const driverName = getDriverName(driverId);
      const displayName = driverName.startsWith('Driver ') ? driverName : `${driverName} (${driverId})`;
      return {
        id: driverId,
        name: driverName,
        displayName: displayName
      };
    }).sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
    
    const uniqueRouteTypes = [...new Set(allData.map(item => item.ROUTE_TYPE_NAME))].filter(Boolean).sort();
    const uniqueDirections = [...new Set(allData.map(item => item.DIRECTION))].filter(Boolean).sort();

    // Trips should still depend on selected route(s)
    let uniqueTrips = [...new Set(allData.map(item => otrDataProcessor.getTripKey(item)))].filter(Boolean);
    if (activeRoutes.length > 0) {
      const routeTrips = new Set();
      allData.forEach(item => {
        const tripKey = otrDataProcessor.getTripKey(item);
        if (activeRoutes.some(route => String(item.ROUTE) === String(route)) && tripKey) {
          routeTrips.add(tripKey);
        }
      });
      uniqueTrips = Array.from(routeTrips);
    }
    uniqueTrips.sort((a, b) => {
      const tripA = tripStartTimes[a];
      const tripB = tripStartTimes[b];
      if (tripA && tripB && tripA.route && tripB.route) {
        const routeComparison = String(tripA.route).localeCompare(String(tripB.route));
        if (routeComparison !== 0) return routeComparison;
      }
      if (tripA && tripB && tripA.time && tripB.time) {
        return tripA.time.localeCompare(tripB.time);
      }
      // Extract sorting key from KEY format
      // New format: ROUTE_VARIANT_STARTTIME_CONTRACT_CODE
      // Legacy format: ROUTE_VARIANT_TRIP_ID
      const extractSortingKey = (key) => {
        if (!key || typeof key !== 'string') return String(key || '');
        const parts = key.split('_');
        
        // For new format, use start time for sorting
        if (parts.length >= 3) {
          const timeCandidate = parts[parts.length - 2];
          if (timeCandidate && timeCandidate.match(/^\d{2}:\d{2}$/)) {
            return timeCandidate; // Return start time for sorting
          }
        }
        
        // For legacy format, use last part (TRIP_ID)
        return parts.length >= 2 ? parts[parts.length - 1] : key;
      };
      const keyA = extractSortingKey(a);
      const keyB = extractSortingKey(b);
      
      // Try numeric comparison first (for legacy TRIP_ID format)
      const numA = Number(keyA);
      const numB = Number(keyB);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      
      // Fall back to string comparison (for time format HH:mm)
      return String(keyA).localeCompare(String(keyB));
    });

    setFilterOptions(prevOptions => ({
      ...prevOptions,
      routes: availableRoutes,
      drivers: driverOptions,
      routeTypes: uniqueRouteTypes,
      directions: uniqueDirections,
      trips: uniqueTrips
    }));
  };

  // Helper function to get time period
  const getTimePeriod = (timeString) => {
    if (!timeString) return 'unknown';
    
    try {
      // Extract time part from datetime string
      let timePart = timeString;
      if (timeString.includes(' ')) {
        timePart = timeString.split(' ')[1];
      }
      
      // Default to empty string if splitting fails
      if (!timePart) return 'unknown';
      
      // Extract hours:minutes
      const timeForComparison = timePart.substring(0, 5);
      
      const TIME_PERIODS = {
        MORNING: { start: '06:00', end: '10:00' },
        MIDDAY: { start: '10:01', end: '15:00' },
        AFTERNOON: { start: '15:01', end: '19:00' },
        EVENING: { start: '19:01', end: '23:59' },
        NIGHT: { start: '00:00', end: '05:59' }
      };
      
      for (const [period, { start, end }] of Object.entries(TIME_PERIODS)) {
        if (timeForComparison >= start && timeForComparison <= end) {
          return period;
        }
      }
      
      return 'unknown';
    } catch (error) {
      console.warn('Error determining time period:', error);
      return 'unknown';
    }
  };

  // Use centralized data processor for trip processing
  const processTripsData = (data) => {
    const result = otrDataProcessor.processTripsData(data, treatSchoolOutEarlyAsOnTime);
    return result;
  };

  // Use centralized data processor for variance calculation
  const getDepartureVariance = (stop) => {
    return otrDataProcessor.getDepartureVariance(stop);
  };

  // Helper to calculate stop statistics
  // Use centralized data processor for stop statistics
  const calculateStopStats = (stops, useSchoolOutRule = true) => {
    return otrDataProcessor.calculateStopStats(stops, useSchoolOutRule ? treatSchoolOutEarlyAsOnTime : false);
  };

  // When user clicks on a trip in Level 1
  const handleTripClick = async (tripKey) => {
    // Find the selected trip
    const trip = tripData.find(t => t.tripKey === tripKey);
    if (!trip) {
      message.error("Trip not found");
      return;
    }

    // Get date-level data for this trip using filtered data
    const dateData = await getTripDateData(tripKey, null);
    setSelectedTrip(trip);
    setViewLevel(VIEW_LEVELS.TRIP_BY_DATE);
    setFilteredData(dateData);
  };

  /**
   * Get trip data by date
   * @param {string} tripKey - The unique trip key to get data for
   * @param {Array} filteredData - Pre-filtered data from parent component (optional, falls back to fetching all data)
   * @returns {Promise<Array>} Array of date-level trip data
   */
  const getTripDateData = async (tripKey, filteredData = null) => {
    let tripRecords = [];
    
    // Use filtered data if provided, otherwise use date-filtered dataset
    if (filteredData && Array.isArray(filteredData)) {
      // Filter for just the current trip from the provided filtered data
      tripRecords = filteredData.filter(r => otrDataProcessor.getTripKey(r) === tripKey);
    } else if (isDateDatasetLoaded && dateFilteredDataset.length > 0) {
      // Filter for selected trip from the date-filtered dataset
      tripRecords = dateFilteredDataset.filter(r => otrDataProcessor.getTripKey(r) === tripKey);
    } else {
      // Get raw data from OTRDataService (fallback)
      const allData = await OTRDataService.retrieveAllData();
      // Filter for selected trip
      tripRecords = allData.filter(r => otrDataProcessor.getTripKey(r) === tripKey);
    }
    
    if (tripRecords.length === 0) {
      console.warn(`No records found for trip ${tripKey}. This will result in empty table.`);
      return [];
    }
    
    // Group by date - ensure we're treating DATE_ID consistently as strings
    const dateGroups = {};
    tripRecords.forEach(record => {
      if (!record.DATE_ID) return;
      
      // Convert DATE_ID to string to ensure consistent grouping
      const dateId = String(record.DATE_ID);
      
      if (!dateGroups[dateId]) {
        dateGroups[dateId] = {
          tripKey: tripKey,
          tripId: record.TRIP_ID, // Keep original TRIP_ID for display
          dateId: dateId,
          route: record.ROUTE,
          date: record.DATE_FORMATTED || dayjs(dateId).format('YYYY-MM-DD'),
          stops: [],
          firstStops: [],
          midStops: [],
          lastStops: []
        };
      }
      
      // Add to appropriate stop category
      if (record.TSN_FIRST_LAST_IND === 'F') {
        dateGroups[dateId].firstStops.push(record);
      } else if (!record.TSN_FIRST_LAST_IND?.length || record.TSN_FIRST_LAST_IND === 'M') {
        dateGroups[dateId].midStops.push(record);
      } else if (record.TSN_FIRST_LAST_IND === 'L') {
        dateGroups[dateId].lastStops.push(record);
      }
      
      // Add to all stops
      dateGroups[dateId].stops.push(record);
    });
    
    // Calculate stats for each date and convert to array
    const result = Object.values(dateGroups).map(dateGroup => {
      const firstStopStats = calculateStopStats(dateGroup.firstStops, treatSchoolOutEarlyAsOnTime);
      const midStopStats = calculateStopStats(dateGroup.midStops, treatSchoolOutEarlyAsOnTime);
      const lastStopStats = calculateStopStats(dateGroup.lastStops, treatSchoolOutEarlyAsOnTime);
      const overallStats = calculateStopStats(dateGroup.stops, treatSchoolOutEarlyAsOnTime);
      
      // Prepare chart data for this date
      const chartData = prepareDateChartData(dateGroup.stops);
      
      return {
        tripKey: dateGroup.tripKey,
        tripId: dateGroup.tripId, // Keep original TRIP_ID for display
        dateId: dateGroup.dateId,
        date: dateGroup.date,
        route: dateGroup.route,
        stopCount: dateGroup.stops.length,
        firstStopStats,
        midStopStats,
        lastStopStats,
        overallStats,
        chartData // Add chart data
      };
    });
    
    return result;
  };
  
  // Helper to prepare chart data for a single date's stops
  const prepareDateChartData = (stops) => {
    // Sort stops by sequence
    const sortedStops = determineStopSequence(stops);
    
    return sortedStops.map((stop, index) => {
      // Prioritise departure variance
      let variance = null;
      
      // First check for departure variance
      if (Number.isFinite(stop.DEPARTURE_TIME_VARIANCE)) {
        variance = stop.DEPARTURE_TIME_VARIANCE;
      } 
      // Then try to calculate from compact departure time fields
      else if (Number.isFinite(stop.A_DEP) && Number.isFinite(stop.S_DEP)) {
        variance = stop.A_DEP - stop.S_DEP;
      }
      // Fall back to arrival variance only if departure variance is not available
      else if (Number.isFinite(stop.ARRIVAL_TIME_VARIANCE)) {
        variance = stop.ARRIVAL_TIME_VARIANCE;
      }
      
      return {
        name: `Stop ${index + 1}`,
        stopId: stop.TSN || '',
        variance: variance,
        sequence: stop.TIME_RECORD_SEQUENCE?.length ? stop.TIME_RECORD_SEQUENCE : (index + 1),
        scheduled: stop.SCHEDULED_DEPARTURE_TIME || stop.SCHEDULED_ARRIVAL_TIME,
        actual: stop.ACTUAL_DEPARTURE_TIME || stop.ACTUAL_ARRIVAL_TIME,
        status: getOnTimeStatus(variance, stop)
      };
    });
  };
  
  // Helper to determine stop sequence for chart data
  const determineStopSequence = (stops) => {
    // First check for TIME_RECORD_SEQUENCE field (from TPT file)
    const hasTimeRecordSequence = stops.some(stop => stop.TIME_RECORD_SEQUENCE?.length);
    
    if (hasTimeRecordSequence) {
      return [...stops].sort((a, b) => {
        // Convert to number in case it's stored as string
        const seqA = Number(a.TIME_RECORD_SEQUENCE || 0);
        const seqB = Number(b.TIME_RECORD_SEQUENCE || 0);
        return seqA - seqB;
      });
    }
    
    // Next try to use any existing sequence data
    const hasSequence = stops.some(stop => stop.SEQUENCE_NUMBER?.length);
    
    if (hasSequence) {
      return [...stops].sort((a, b) => {
        const seqA = Number(a.SEQUENCE_NUMBER || 0);
        const seqB = Number(b.SEQUENCE_NUMBER || 0);
        return seqA - seqB;
      });
    }
    
    // If we have STOP_SEQUENCE field (another common field in transit data)
    const hasStopSequence = stops.some(stop => stop.STOP_SEQUENCE?.length);
    
    if (hasStopSequence) {
      return [...stops].sort((a, b) => {
        const seqA = Number(a.STOP_SEQUENCE || 0);
        const seqB = Number(b.STOP_SEQUENCE || 0);
        return seqA - seqB;
      });
    }
    
    // Infer sequence based on first/mid/last indicators and scheduled times
    let firstStops = stops.filter(stop => stop.TSN_FIRST_LAST_IND === 'F');
    let midStops = stops.filter(stop => !stop.TSN_FIRST_LAST_IND?.length || stop.TSN_FIRST_LAST_IND === 'M');
    let lastStops = stops.filter(stop => stop.TSN_FIRST_LAST_IND === 'L');
    
    // Sort first, mid, and last stops by scheduled time if available
    if (firstStops.length > 0) {
      firstStops = sortStopsByTime(firstStops);
    }
    
    if (midStops.length > 0) {
      midStops = sortStopsByTime(midStops);
    }
    
    if (lastStops.length > 0) {
      lastStops = sortStopsByTime(lastStops);
    }
    
    // Combine and add sequence numbers
    const sortedStops = [...firstStops, ...midStops, ...lastStops].map((stop, index) => ({
      ...stop,
      SEQUENCE_NUMBER: index + 1
    }));
    
    return sortedStops;
  };
  
  // Helper to sort stops by scheduled time
  const sortStopsByTime = (stops) => {
    return [...stops].sort((a, b) => {
      // Try different time fields in order of preference
      const timeA = a.SCHEDULED_ARRIVAL_TIME || a.SCHEDULED_DEPARTURE_TIME || 0;
      const timeB = b.SCHEDULED_ARRIVAL_TIME || b.SCHEDULED_DEPARTURE_TIME || 0;
      return timeA - timeB;
    });
  };

  // When user clicks on a date in Level 2
  const handleDateClick = (tripKey, dateId) => {
    setSelectedDate(dateId);
    setViewLevel(VIEW_LEVELS.TRIP_DETAIL);
    
    // Log for debugging
    console.log(`Transitioning to trip detail view for tripKey=${tripKey}, dateId=${dateId}`);
  };

  // When user clicks on a stop in the Stop Level view
  const handleStopClick = async (tsn) => {
    console.log(`Transitioning to stop detail view for stop TSN=${tsn}`);
    
    // Get all data about this stop from the date-filtered dataset
    let tripsWithStop = [];
    if (isDateDatasetLoaded && dateFilteredDataset.length > 0) {
      tripsWithStop = dateFilteredDataset.filter(record => record.TSN === tsn);
    } else {
      // Fallback to service call
    const allData = await OTRDataService.retrieveAllData();
      tripsWithStop = allData.filter(record => record.TSN === tsn);
    }
    
    // Group stop data to get statistics
    const stopStats = {};
    
    stopData.forEach(stop => {
      const stopTsn = stop.TSN;
      if (!stopTsn) return;
      
      if (!stopStats[stopTsn]) {
        stopStats[stopTsn] = {
          tsn: stopTsn,
          stopName: stop.STOP_NAME || `Stop ${stopTsn}`
        };
      }
    });
    
    // Get the stop details from stop data if available (more accurate)
    const stopFromStatsArray = Object.values(stopStats).find(s => String(s.tsn) === String(tsn));
    const stopName = stopFromStatsArray ? stopFromStatsArray.stopName : '';
    
    // Create a more complete stop object
    const selectedStopObj = { 
      TSN: tsn,
      STOP_NAME: stopName
    };
    
    setSelectedStop(selectedStopObj);
    setViewLevel(VIEW_LEVELS.STOP_DETAIL);
    
    // Get unique trip IDs
    const uniqueTripIds = [...new Set(tripsWithStop.map(record => record.TRIP_ID))];
    console.log(`Found ${uniqueTripIds.length} trips that include stop ${tsn}`);
    
    // Create a map of trip ID to stop type (First, Mid, Last) for this stop
    const tripStopTypeMap = {};
    tripsWithStop.forEach(record => {
      if (record.TRIP_ID && record.TSN_FIRST_LAST_IND) {
        let typeLabel = '';
        if (record.TSN_FIRST_LAST_IND === 'F') typeLabel = 'First';
        else if (record.TSN_FIRST_LAST_IND === 'M') typeLabel = 'Mid';
        else if (record.TSN_FIRST_LAST_IND === 'L') typeLabel = 'Last';
        
        if (typeLabel) {
          tripStopTypeMap[record.TRIP_ID] = typeLabel;
        }
      }
    });
    
    // Filter trip data to only include trips with this stop and add stop type information
    const stopsFilteredTrips = tripData
      .filter(trip => uniqueTripIds.includes(trip.tripId))
      .map(trip => ({
        ...trip,
        stopType: tripStopTypeMap[trip.tripId] || 'Unknown'
      }));
    
    setFilteredData(stopsFilteredTrips);
    
    // Switch to the trip tab if not already there
    if (activeTab !== 'trip') {
      setActiveTab('trip');
    }
  };

  // Handle going back to previous view level
  const handleBack = async () => {
    if (viewLevel === VIEW_LEVELS.TRIP_BY_DATE) {
      // Go back to Level 1
      setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
      setSelectedTrip(null);
      setFilteredData(tripData);
    } else if (viewLevel === VIEW_LEVELS.TRIP_DETAIL) {
      // Go back to Level 2
      setViewLevel(VIEW_LEVELS.TRIP_BY_DATE);
      setSelectedDate(null);
      // Refresh the date-level data using filtered data
      if (selectedTrip) {
        const dateData = await getTripDateData(selectedTrip.tripKey, null);
        setFilteredData(dateData);
      }
    } else if (viewLevel === VIEW_LEVELS.STOP_DETAIL) {
      // Go back to Stop Level view
      setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
      setSelectedStop(null);
      setFilteredData(tripData);
      // Switch back to stop tab
      setActiveTab('stop');
    }
  };

  // Add missing filter handler functions
  const handleRouteTypeChange = (values) => {
    setSelectedRouteTypes(values);
    setActiveRouteTypes(values);
  };

  const handleDirectionChange = (values) => {
    setSelectedDirections(values);
    setActiveDirections(values);
  };

  const handleRouteChange = (values) => {
    setSelectedRoutes(values);
    setActiveRoutes(values);
  };

  const handleTripFilterChange = (values) => {
    setSelectedTripFilters(values);
    setActiveTripFilters(values);
  };

  const handleDriverChange = (values) => {
    setSelectedDrivers(values);
    setActiveDrivers(values);
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setSelectedRouteTypes([]);
    setActiveRouteTypes([]);
    setSelectedDirections([]);
    setActiveDirections([]);
    setSelectedRoutes([]);
    setActiveRoutes([]);
    setSelectedTripFilters([]);
    setActiveTripFilters([]);
    setSelectedDrivers([]);
    setActiveDrivers([]);
    setSelectedTimePeriod('all');
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      selectedRouteTypes.length > 0 ||
      selectedDirections.length > 0 ||
      selectedRoutes.length > 0 ||
      selectedTripFilters.length > 0 ||
      selectedDrivers.length > 0 ||
      selectedTimePeriod !== 'all'
    );
  };

  // Render active filter summary
  const renderActiveFilterSummary = () => {
    const activeFilters = [];
    
    if (activeRouteTypes.length > 0) {
      activeFilters.push(`Route Types: ${activeRouteTypes.length}`);
    }
    if (activeDirections.length > 0) {
      activeFilters.push(`Directions: ${activeDirections.length}`);
    }
    if (activeRoutes.length > 0) {
      activeFilters.push(`Routes: ${activeRoutes.length}`);
    }
    if (activeTripFilters.length > 0) {
      activeFilters.push(`Trips: ${activeTripFilters.length}`);
    }
    if (activeDrivers.length > 0) {
      activeFilters.push(`Drivers: ${activeDrivers.length}`);
    }
    
    if (activeFilters.length === 0) {
    return (
        <Text type="secondary" style={{ fontSize: '11px' }}>
          No filters applied
        </Text>
      );
    }
    
    return (
      <Text type="secondary" style={{ fontSize: '11px' }}>
        Active filters: {activeFilters.join(' • ')}
      </Text>
    );
  };

  // Columns for Level 1: Trip Aggregate View
  const tripColumns = [
    {
      title: 'Trip Start',
      dataIndex: 'tripKey',
      key: 'tripKey',
      sorter: (a, b) => {
        // Extract and compare original TRIP_ID for sorting
        const numA = Number(a.tripId || 0);
        const numB = Number(b.tripId || 0);
        return numA - numB;
      },
      render: (tripKey, record) => {
        const startTime = tripStartTimes[tripKey]?.time;
        return (
        <Button 
          type="link" 
          style={{ 
            padding: 0, 
            fontSize: '14px', 
            fontWeight: 'bold',
            textDecoration: 'underline'
          }}
            onClick={() => handleTripClick(tripKey)}
        >
            {startTime || 'undefined'} ({record.tripId})
        </Button>
        );
      },
      width: 100,
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      sorter: (a, b) => {
        // Handle both numeric and string routes
        const routeA = a.route;
        const routeB = b.route;
        if (typeof routeA === 'number' && typeof routeB === 'number') {
          return routeA - routeB;
        }
        return String(routeA || '').localeCompare(String(routeB || ''));
      },
      filters: filterOptions.routes.map(route => ({
        text: String(route),
        value: route,
      })),
      onFilter: (value, record) => {
        // Handle both numeric and string values
        if (typeof record.route === 'number' && typeof value === 'string') {
          return record.route === Number(value);
        }
        return String(record.route) === String(value);
      },
      width: 80,
    },
    {
      title: 'Trips',
      dataIndex: 'dateCount',
      key: 'dateCount',
      sorter: (a, b) => a.dateCount - b.dateCount,
      width: 60,
      render: (count) => (
        <Tooltip title="Number of days this trip was operated">
          <span>{count}</span>
        </Tooltip>
      ),
    },
    {
      title: 'First OTR %',
      dataIndex: ['firstStopStats', 'onTimePercent'],
      key: 'firstStopOTR',
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue'; // Late - blue
        else if (value < 90) color = 'red'; // Early - red
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(value)}%</div>
              <div>Early: {Math.round(record.firstStopStats?.earlyPercent || 0)}%</div>
              <div>Late: {Math.round(record.firstStopStats?.latePercent || 0)}%</div>
              <div>Total Stops: {record.firstStopStats?.totalStops || 0}</div>
            </div>
          }>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.firstStopStats?.onTimePercent || 0) - (b.firstStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Mid OTR %',
      key: 'midStopOTR',
      dataIndex: ['midStopStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue'; // Late - blue
        else if (value < 90) color = 'red'; // Early - red
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(value)}%</div>
              <div>Early: {Math.round(record.midStopStats?.earlyPercent || 0)}%</div>
              <div>Late: {Math.round(record.midStopStats?.latePercent || 0)}%</div>
              <div>Total Stops: {record.midStopStats?.totalStops || 0}</div>
            </div>
          }>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.midStopStats?.onTimePercent || 0) - (b.midStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Last OTR %',
      key: 'lastStopOTR',
      dataIndex: ['lastStopStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue'; // Late - blue
        else if (value < 90) color = 'red'; // Early - red
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(value)}%</div>
              <div>Early: {Math.round(record.lastStopStats?.earlyPercent || 0)}%</div>
              <div>Late: {Math.round(record.lastStopStats?.latePercent || 0)}%</div>
              <div>Total Stops: {record.lastStopStats?.totalStops || 0}</div>
            </div>
          }>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.lastStopStats?.onTimePercent || 0) - (b.lastStopStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Overall OTR %',
      key: 'overallOTR',
      dataIndex: ['overallStats', 'onTimePercent'],
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue'; // Late - blue
        else if (value < 90) color = 'red'; // Early - red
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(value)}%</div>
              <div>Early: {Math.round(record.overallStats?.earlyPercent || 0)}%</div>
              <div>Late: {Math.round(record.overallStats?.latePercent || 0)}%</div>
              <div>Total Stops: {record.overallStats?.totalStops || 0}</div>
              <div>Average Variance: {toHrsMinsSecs(record.overallStats?.avgVariance || 0)}</div>
            </div>
          }>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.overallStats?.onTimePercent || 0) - (b.overallStats?.onTimePercent || 0),
      width: 120,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const value = record.overallStats?.onTimePercent || 0;
        let color = 'green';
        let text = 'Good';
        
        if (value < 70) {
          color = 'blue';
          text = 'Poor';
        } else if (value < 90) {
          color = 'orange';
          text = 'Fair';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
      width: 80,
    }
  ];

  // Columns for Level 2: Trip by Date View
  const tripDateColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => String(a.dateId || '').localeCompare(String(b.dateId || '')),
      render: (date, record) => (
        <Button 
          type="link" 
          onClick={() => handleDateClick(record.tripKey, record.dateId)}
          style={{ padding: 0 }}
        >
          {date}
        </Button>
      ),
      width: 100,
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      width: 80,
    },
    {
      title: 'Stop Count',
      dataIndex: 'stopCount',
      key: 'stopCount',
      sorter: (a, b) => a.stopCount - b.stopCount,
      width: 80,
      render: (count) => (
        <Tooltip title="Number of stops in this trip">
          <span>{count}</span>
        </Tooltip>
      ),
    },
    {
      title: 'First Stop OTR',
      dataIndex: 'firstStopStats',
      key: 'firstStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        // Determine the dominant status
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        if (onTimePercent < 70) color = 'blue';
        else if (onTimePercent < 90) color = 'orange';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(onTimePercent)}%</div>
              <div>Early: {Math.round((earlyCount / total) * 100)}%</div>
              <div>Late: {Math.round((lateCount / total) * 100)}%</div>
              <div>Total Stops: {total}</div>
            </div>
          }>
            <Tag color={color}>{onTimePercent >= 90 ? 'Good' : onTimePercent >= 70 ? 'Fair' : 'Poor'}</Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const getOTRValue = (stats) => {
          if (!stats) return 0;
          const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
          const total = earlyCount + onTimeCount + lateCount;
          if (total === 0) return 0;
          return onTimeCount / total;
        };
        return getOTRValue(a.firstStopStats) - getOTRValue(b.firstStopStats);
      },
      width: 100,
    },
    {
      title: 'Mid Stop OTR',
      dataIndex: 'midStopStats',
      key: 'midStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        // Determine the dominant status
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        if (onTimePercent < 70) color = 'blue';
        else if (onTimePercent < 90) color = 'orange';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(onTimePercent)}%</div>
              <div>Early: {Math.round((earlyCount / total) * 100)}%</div>
              <div>Late: {Math.round((lateCount / total) * 100)}%</div>
              <div>Total Stops: {total}</div>
            </div>
          }>
            <Tag color={color}>{onTimePercent >= 90 ? 'Good' : onTimePercent >= 70 ? 'Fair' : 'Poor'}</Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const getOTRValue = (stats) => {
          if (!stats) return 0;
          const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
          const total = earlyCount + onTimeCount + lateCount;
          if (total === 0) return 0;
          return onTimeCount / total;
        };
        return getOTRValue(a.midStopStats) - getOTRValue(b.midStopStats);
      },
      width: 100,
    },
    {
      title: 'Last Stop OTR',
      dataIndex: 'lastStopStats',
      key: 'lastStopOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        // Determine the dominant status
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        if (onTimePercent < 70) color = 'blue';
        else if (onTimePercent < 90) color = 'orange';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(onTimePercent)}%</div>
              <div>Early: {Math.round((earlyCount / total) * 100)}%</div>
              <div>Late: {Math.round((lateCount / total) * 100)}%</div>
              <div>Total Stops: {total}</div>
            </div>
          }>
            <Tag color={color}>{onTimePercent >= 90 ? 'Good' : onTimePercent >= 70 ? 'Fair' : 'Poor'}</Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const getOTRValue = (stats) => {
          if (!stats) return 0;
          const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
          const total = earlyCount + onTimeCount + lateCount;
          if (total === 0) return 0;
          return onTimeCount / total;
        };
        return getOTRValue(a.lastStopStats) - getOTRValue(b.lastStopStats);
      },
      width: 100,
    },
    {
      title: 'Overall OTR',
      dataIndex: 'overallStats',
      key: 'overallOTR',
      render: stats => {
        if (!stats || !stats.earlyCount && !stats.onTimeCount && !stats.lateCount) {
          return <Tag color="default">Unknown</Tag>;
        }

        // Determine the dominant status
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        if (onTimePercent < 70) color = 'blue';
        else if (onTimePercent < 90) color = 'orange';
        
        return (
          <Tooltip title={
            <div>
              <div>On Time: {Math.round(onTimePercent)}%</div>
              <div>Early: {Math.round((earlyCount / total) * 100)}%</div>
              <div>Late: {Math.round((lateCount / total) * 100)}%</div>
              <div>Total Stops: {total}</div>
              <div>Average Variance: {toHrsMinsSecs(stats.avgVariance || 0)}</div>
            </div>
          }>
            <Tag color={color}>{onTimePercent >= 90 ? 'Good' : onTimePercent >= 70 ? 'Fair' : 'Poor'}</Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const getOTRValue = (stats) => {
          if (!stats) return 0;
          const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
          const total = earlyCount + onTimeCount + lateCount;
          if (total === 0) return 0;
          return onTimeCount / total;
        };
        return getOTRValue(a.overallStats) - getOTRValue(b.overallStats);
      },
      width: 100,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const stats = record.overallStats;
        if (!stats) return <Tag color="default">Unknown</Tag>;
        
        const { earlyCount = 0, onTimeCount = 0, lateCount = 0 } = stats;
        const total = earlyCount + onTimeCount + lateCount;
        const onTimePercent = (onTimeCount / total) * 100;
        
        let color = 'green';
        let text = 'Good';
        
        if (onTimePercent < 70) {
          color = 'blue';
          text = 'Poor';
        } else if (onTimePercent < 90) {
          color = 'orange';
          text = 'Fair';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
      width: 80,
    }
  ];

  // Handle import completion
  const handleImportComplete = async (importedData) => {
    // Process and store imported data
    try {
      const metadata = await OTRDataService.storeData(importedData);
      
      // Update filter options
      setFilterOptions(prevOptions => ({
        ...prevOptions,
        routes: metadata.routes || [],
        drivers: metadata.drivers || [],
        contracts: metadata.contracts || [],
        routeTypes: metadata.routeTypes || [],
        directions: metadata.directions || []
      }));
      
      // Reset date dataset loaded flag to force reload
      setIsDateDatasetLoaded(false);
      setDateFilteredDataset([]);
      
      // Load data with current date range
      if (dateRange && dateRange[0] && dateRange[1]) {
        loadDateFilteredData();
      }

      // Hide importer
      setShowImporter(false);
      
      message.success(`Successfully stored ${metadata.totalRecords} OTR records for analysis`);
    } catch (error) {
      console.error('Error storing imported data:', error);
      message.error(`Failed to store imported data: ${error.message}`);
    }
  };

  // Render navigation breadcrumb
  const renderBreadcrumb = () => {
    return (
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <a onClick={() => {
            setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
            setSelectedTrip(null);
            setSelectedDate(null);
            setSelectedStop(null);
            setFilteredData(tripData);
          }}>
            <HomeOutlined /> Trips
          </a>
        </Breadcrumb.Item>
        
        {viewLevel >= VIEW_LEVELS.TRIP_BY_DATE && selectedTrip && (
          <Breadcrumb.Item>
            <a onClick={() => {
              setViewLevel(VIEW_LEVELS.TRIP_BY_DATE);
              setSelectedDate(null);
              getTripDateData(selectedTrip.tripKey, stopData).then(setFilteredData);
            }}>
              {selectedTrip.route} Trip {tripStartTimes[selectedTrip.tripKey]?.time} ({selectedTrip.tripId})
            </a>
          </Breadcrumb.Item>
        )}
        
        {viewLevel === VIEW_LEVELS.TRIP_DETAIL && selectedDate && (
          <Breadcrumb.Item>
            Date {dayjs(String(selectedDate)).format('YYYY-MM-DD')}
          </Breadcrumb.Item>
        )}

        {viewLevel === VIEW_LEVELS.STOP_DETAIL && selectedStop && (
          <Breadcrumb.Item>
            Stop {selectedStop.TSN} {selectedStop.STOP_NAME && `(${selectedStop.STOP_NAME})`}
          </Breadcrumb.Item>
        )}
      </Breadcrumb>
    );
  };





  // Add the handler function for date preset changes
  const handleDatePresetChange = (preset) => {
    setSelectedDatePreset(preset);
    const today = dayjs();
    let startDate, endDate;
    switch (preset) {
      case 'last30':
        startDate = today.subtract(30, 'day');
        endDate = today.endOf('day');
        break;
      case 'last60':
        startDate = today.subtract(60, 'day');
        endDate = today.endOf('day');
        break;
      case 'thisQuarter':
        startDate = today.startOf('quarter');
        endDate = today.endOf('quarter').endOf('day');
        break;
      case 'lastQuarter':
        startDate = today.subtract(1, 'quarter').startOf('quarter');
        endDate = today.subtract(1, 'quarter').endOf('quarter').endOf('day');
        break;
      case 'custom':
        return;
      default:
        startDate = today.subtract(30, 'day');
        endDate = today.endOf('day');
    }
    setDateRange([startDate, endDate]);
  };

  // Use centralized data processor for on-time status
  const getOnTimeStatus = (variance, stop = null) => {
    return otrDataProcessor.getOnTimeStatus(variance, stop, treatSchoolOutEarlyAsOnTime);
  };

  // Use centralized data processor for school rule check
  const shouldTreatEarlyAsOnTime = (stop) => {
    return otrDataProcessor.shouldTreatEarlyAsOnTime(stop, treatSchoolOutEarlyAsOnTime);
  };









  const handleDriverClick = async (driverId) => {
    setSelectedDriverDetail(driverId);
    // Filter trip data for this driver from already filtered tripData
    // tripData is processed from filtered data in loadData(), so this maintains filter consistency
    const driverTrips = tripData.filter(trip => trip.driverId === driverId);
    setDriverTripData(driverTrips);
  };

  const renderDriverTripView = () => {
    if (!selectedDriverDetail || driverTripData.length === 0) {
      return null;
    }

    const columns = [
      {
        title: 'Trip Start',
        dataIndex: 'tripId',
        key: 'tripId',
        sorter: (a, b) => String(a.tripId || '').localeCompare(String(b.tripId || '')),
        render: (_, record) => tripStartTimes[record.tripKey]?.time  ,
      },
      {
        title: 'Route',
        dataIndex: 'route',
        key: 'route',
        sorter: (a, b) => String(a.route || '').localeCompare(String(b.route || '')),
      },
      {
        title: 'On-Time %',
        dataIndex: ['overallStats', 'onTimePercent'],
        key: 'onTimePercent',
        render: val => {
          const value = val || 0;
          let color = 'green';
          if (value < 70) color = 'blue';
          else if (value < 90) color = 'red';
          
          return (
            <Tooltip title={`${Math.round(value)}% on time`}>
              <Progress 
                percent={Math.round(value)} 
                size="small" 
                strokeColor={color}
                format={percent => `${percent}%`}
              />
            </Tooltip>
          );
        },
        sorter: (a, b) => (a.overallStats?.onTimePercent || 0) - (b.overallStats?.onTimePercent || 0),
      },
      {
        title: 'First Stop OTR',
        dataIndex: ['firstStopStats', 'onTimePercent'],
        key: 'firstStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.firstStopStats?.onTimePercent || 0) - (b.firstStopStats?.onTimePercent || 0),
      },
      {
        title: 'Middle Stop OTR',
        dataIndex: ['midStopStats', 'onTimePercent'],
        key: 'midStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.midStopStats?.onTimePercent || 0) - (b.midStopStats?.onTimePercent || 0),
      },
      {
        title: 'Last Stop OTR',
        dataIndex: ['lastStopStats', 'onTimePercent'],
        key: 'lastStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.lastStopStats?.onTimePercent || 0) - (b.lastStopStats?.onTimePercent || 0),
      },
      {
        title: 'Total Stops',
        dataIndex: 'stopCount',
        key: 'stopCount',
        sorter: (a, b) => (a.stopCount || 0) - (b.stopCount || 0),
      }
    ];

    return (
      <Card 
        title={`Trip Performance for Driver ${selectedDriverDetail}`}
        extra={
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => setSelectedDriverDetail(null)}
          >
            Back to Drivers
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={driverTripData}
          rowKey="tripId"
          pagination={{ pageSize: 20 }}
        />
      </Card>
    );
  };

  // Create a function to render global filters (now only common filters)
  const renderGlobalFilters = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Row style={{ width: '100%' }} justify="space-between" align="middle">
          <Col flex="320px">
            <OTRDateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              selectedPreset={selectedDatePreset}
              onPresetChange={handleDatePresetChange}
            />
          </Col>
          
          <Col flex="auto" style={{ display: 'flex', gap: '8px', marginLeft: '16px', marginRight: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <Select
                  placeholder="Route Type"
                  style={{ width: '120px', minWidth: '120px' }}
                  value={selectedRouteTypes}
                  onChange={handleRouteTypeChange}
                  mode="multiple"
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                  popupMatchSelectWidth={false}
                  maxTagCount={1}
                  options={filterOptions.routeTypes.map(routeType => ({
                    label: routeType,
                    value: routeType
                  }))}
                />
                
                <Select
                  placeholder="Direction"
                  style={{ width: '110px', minWidth: '110px' }}
                  value={selectedDirections}
                  onChange={handleDirectionChange}
                  mode="multiple"
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                  popupMatchSelectWidth={false}
                  maxTagCount={1}
                  options={filterOptions.directions.map(direction => ({
                    label: direction,
                    value: direction
                  }))}
                />
                
            <Select
              placeholder="Route"
              style={{ width: '110px', minWidth: '110px' }}
                  value={selectedRoutes}
                  onChange={handleRouteChange}
                  mode="multiple"
              allowClear
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                  popupMatchSelectWidth={false}
                  maxTagCount={1}
                  options={filterOptions.routes.map(route => ({
                    label: route,
                    value: route
                  }))}
                />
                
                {selectedRoutes.length > 0 && (
                  <Select
                    placeholder="Trip"
                    style={{ width: '140px', minWidth: '140px' }}
                    value={selectedTripFilters}
                    onChange={handleTripFilterChange}
                    mode="multiple"
                    allowClear
                    showSearch
                    filterOption={(input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                    popupMatchSelectWidth={false}
                    maxTagCount={1}
                                      options={filterOptions.trips.map(tripKey => {
                    const tripInfo = tripStartTimes[tripKey];
                    let displayText = tripKey;
                      
                      if (tripInfo && tripInfo.time && tripInfo.route) {
                        displayText = `${tripInfo.route} : ${tripInfo.time}`;
                      } else if (tripInfo && tripInfo.time) {
                      displayText = `${tripInfo.tripId || tripKey} : ${tripInfo.time}`;
                      }
                      
                      return {
                        label: displayText,
                      value: tripKey
                      };
                    })}
                  />
                )}
            
            <Select
              placeholder="Driver"
              style={{ width: '200px', minWidth: '200px' }}
                  value={selectedDrivers}
                  onChange={handleDriverChange}
                  mode="multiple"
              allowClear
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                  popupMatchSelectWidth={false}
                  maxTagCount={1}
                  options={filterOptions.drivers.map(driver => ({
                    label: driver.displayName,
                    value: driver.id
                  }))}
                />
            
                {hasActiveFilters() && (
            <Button 
                    onClick={handleClearAllFilters}
              type="primary" 
                    size="small"
                    className="icon-button btn-filled icon-10 btn-xs"
                    style={{ fontSize: '11px', padding: '4px 8px', height: '32px', marginLeft: 'auto' }}
                  >
                    <Close style={{ width: '12px', height: '12px', marginRight: '4px' }} />
                    Clear Filters
            </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  };



  return (
    <div>
      {/* Header Card with Title and Filters - Above all tabs */}
      <Card 
        title="OTR Dashboard" 
        extra={<Text type="secondary">On-Time Running Performance Analysis</Text>}
        style={{ marginBottom: '16px' }}
      >
        {/* Add global filters */}
        {renderGlobalFilters()}
      </Card>
      
      {/* Main Top-Level Tabs */}
        <Tabs 
        activeKey={mainTab}
        onChange={setMainTab}
          size="large"
        style={{ marginBottom: '0px' }}
        tabBarStyle={{ marginBottom: '24px', borderBottom: '2px solid #f0f0f0' }}
        >
          <TabPane 
          tab={<span style={{ fontSize: '18px', padding: '0 16px', fontWeight: '600' }}><BarChartOutlined style={{ marginRight: '8px' }} />OTR</span>} 
          key="otr"
        >
          {mainTab === 'otr' && (
            <OTRTabs
              // State props
              loading={loading}
              showImporter={showImporter}
              tripData={tripData}
              stopData={stopData}
              driverData={driverData}
              filteredData={filteredData}
              viewLevel={viewLevel}
              selectedTrip={selectedTrip}
              selectedDate={selectedDate}
              selectedStop={selectedStop}
              selectedStopTypes={selectedStopTypes}
              activeTab={activeTab}
              dateRange={dateRange}
              tripStartTimes={tripStartTimes}
              reportData={reportData}
              dateFilteredDataset={dateFilteredDataset}
              isDateDatasetLoaded={isDateDatasetLoaded}
              treatSchoolOutEarlyAsOnTime={treatSchoolOutEarlyAsOnTime}
              
              // Setter props
              setActiveTab={setActiveTab}
              setViewLevel={setViewLevel}
              setSelectedTrip={setSelectedTrip}
              setSelectedDate={setSelectedDate}
              setFilteredData={setFilteredData}
              setSelectedStopTypes={setSelectedStopTypes}
              setTreatSchoolOutEarlyAsOnTime={setTreatSchoolOutEarlyAsOnTime}
              
              // Function props
              handleTripClick={handleTripClick}
              handleDateClick={handleDateClick}
              handleBack={handleBack}
              handleStopClick={handleStopClick}
              filterDataByStopType={filterDataByStopType}
              shouldTreatEarlyAsOnTime={shouldTreatEarlyAsOnTime}
              
              // Components that need to be passed
              OTRImporter={<OTRImporter onImportComplete={handleImportComplete} />}
              renderBreadcrumb={renderBreadcrumb}
              OTR_THRESHOLDS={OTR_THRESHOLDS}
            />
            )}
          </TabPane>
        
          <TabPane 
          tab={<span style={{ fontSize: '18px', padding: '0 16px', fontWeight: '600' }}><FieldTimeOutlined style={{ marginRight: '8px' }} />Tracked</span>} 
          key="tracked"
        >
          {mainTab === 'tracked' && (
            <TrackedTab
              // State props
              loading={loading}
              tripData={tripData}
              stopData={stopData}
              driverData={driverData}
              filteredData={filteredData}
              viewLevel={viewLevel}
              selectedTrip={selectedTrip}
              selectedDate={selectedDate}
              selectedStop={selectedStop}
              selectedStopTypes={selectedStopTypes}
              dateRange={dateRange}
              tripStartTimes={tripStartTimes}
              reportData={reportData}
              dateFilteredDataset={dateFilteredDataset}
              isDateDatasetLoaded={isDateDatasetLoaded}
              
              // Setter props
              setViewLevel={setViewLevel}
              setSelectedTrip={setSelectedTrip}
              setSelectedDate={setSelectedDate}
              setFilteredData={setFilteredData}
              setSelectedStopTypes={setSelectedStopTypes}
              
              // Function props
              handleTripClick={handleTripClick}
              handleDateClick={handleDateClick}
              handleBack={handleBack}
              handleStopClick={handleStopClick}
              filterDataByStopType={filterDataByStopType}
              shouldTreatEarlyAsOnTime={shouldTreatEarlyAsOnTime}
              treatSchoolOutEarlyAsOnTime={treatSchoolOutEarlyAsOnTime}
            />
            )}
          </TabPane>
        </Tabs>
    </div>
  );
};

export default OTRDashboard; 